import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Theater, MapPin, Users, Ruler, Search, Filter,
  ArrowRight, Loader2, Calendar,
} from "lucide-react";

/**
 * Public directory for L18 Theater Halls — theater groups (and any
 * signed-in user) can browse & request a booking.
 */
export default function TheaterHallDiscovery() {
  const [params, setParams] = useSearchParams();
  const [halls, setHalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stageCatalog, setStageCatalog] = useState([]);
  const [productions, setProductions] = useState([]);
  const [me, setMe] = useState(null);
  const [showBook, setShowBook] = useState(null);
  const [q, setQ] = useState(params.get("q") || "");
  const [city, setCity] = useState(params.get("city") || "");
  const [stageType, setStageType] = useState(params.get("stage_type") || "");

  useEffect(() => {
    api.get("/meta/theater-hall-stage-types")
      .then(({ data }) => setStageCatalog(Array.isArray(data) ? data : []))
      .catch(() => {});
    api.get("/auth/me").then(({ data }) => setMe(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (me?.account_type === "theater_group_admin") {
      api.get("/theater-group/productions/mine")
        .then(({ data }) => setProductions(data.productions || []))
        .catch(() => {});
    }
  }, [me?.account_type]);

  const load = () => {
    setLoading(true);
    const search = {};
    if (q.trim()) search.q = q.trim();
    if (city.trim()) search.city = city.trim();
    if (stageType) search.stage_type = stageType;
    api.get("/theater-halls", { params: search })
      .then(({ data }) => setHalls(data.halls || []))
      .catch(() => setHalls([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);   

  const applyFilters = () => {
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    if (city.trim()) p.set("city", city.trim());
    if (stageType) p.set("stage_type", stageType);
    setParams(p);
    load();
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10"
         data-testid="theater-hall-discovery">
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">
        Discover · L18 Theater Halls
      </div>
      <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2 text-white">
        Find a Venue
      </h1>
      <p className="mt-3 text-sm text-white/60 max-w-2xl leading-relaxed">
        Every listed theater hall in India — filter by city and stage type, book a date, message the venue.
      </p>

      {/* FILTER BAR */}
      <div className="mt-8 cs-card rounded-2xl p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-[10px] uppercase tracking-[0.22em] text-white/45">Search</Label>
          <div className="relative mt-1.5">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"/>
            <Input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Name / city…"
                   className="bg-black/40 border-white/15 h-10 pl-9"
                   data-testid="theater-halls-q"/>
          </div>
        </div>
        <div className="w-48">
          <Label className="text-[10px] uppercase tracking-[0.22em] text-white/45">City</Label>
          <Input value={city} onChange={(e) => setCity(e.target.value)}
                 placeholder="Mumbai / Bengaluru…"
                 className="bg-black/40 border-white/15 h-10 mt-1.5"
                 data-testid="theater-halls-city"/>
        </div>
        <div className="w-52">
          <Label className="text-[10px] uppercase tracking-[0.22em] text-white/45">Stage Type</Label>
          <select value={stageType} onChange={(e) => setStageType(e.target.value)}
                  className="w-full h-10 mt-1.5 px-3 rounded-md bg-black/40 border border-white/15 text-sm text-white"
                  data-testid="theater-halls-stage-type">
            <option value="">All types</option>
            {stageCatalog.map((s) => (<option key={s.key} value={s.key}>{s.label}</option>))}
          </select>
        </div>
        <Button onClick={applyFilters}
                className="h-10 rounded-full text-[10px] uppercase tracking-[0.22em] bg-violet-500 text-white hover:bg-violet-400 px-5"
                data-testid="theater-halls-apply">
          <Filter size={12} className="mr-2"/> Filter
        </Button>
      </div>

      {/* RESULTS */}
      <div className="mt-8">
        {loading ? (
          <div className="cs-card rounded-xl p-10 text-center text-white/50">
            <Loader2 size={16} className="animate-spin inline mr-2"/> loading halls…
          </div>
        ) : halls.length === 0 ? (
          <div className="cs-card rounded-xl p-10 text-center" data-testid="theater-halls-empty">
            <Theater size={30} className="mx-auto text-white/25 mb-4"/>
            <div className="text-white/70 text-sm">No halls found for these filters.</div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="theater-halls-list">
            {halls.map((h) => (
              <HallCard key={h.id} hall={h}
                        stageCatalog={stageCatalog}
                        canBook={me?.account_type === "theater_group_admin"}
                        onBook={() => setShowBook(h)}/>
            ))}
          </div>
        )}
      </div>

      {showBook && (
        <BookHallModal
          hall={showBook}
          productions={productions}
          onClose={() => setShowBook(null)}
          onBooked={() => { setShowBook(null); toast.success("Booking request sent. The venue will respond soon."); }}
        />
      )}
    </div>
  );
}

function HallCard({ hall, stageCatalog, canBook, onBook }) {
  const stageLabel = stageCatalog.find((s) => s.key === hall.theater_hall_stage_type)?.label
                      || hall.theater_hall_stage_type || "—";
  const accent = "#7C3AED";
  return (
    <div className="cs-card rounded-xl p-5" data-testid={`theater-hall-card-${hall.id}`}>
      <div className="w-full h-32 rounded-lg mb-4 flex items-center justify-center"
           style={{ background: `${accent}15` }}>
        <Theater size={36} style={{ color: accent }}/>
      </div>
      <div className="font-display text-lg uppercase tracking-tight text-white">
        {hall.brand_name || hall.legal_name}
      </div>
      <div className="mt-1 text-[11px] text-white/60">
        <MapPin size={9} className="inline mr-1"/>
        {hall.registered_city}{hall.registered_state ? `, ${hall.registered_state}` : ""}
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        <span className="px-2 py-0.5 rounded-full text-[9px] tracking-[0.18em] uppercase"
              style={{ background: `${accent}22`, color: accent }}>
          {stageLabel}
        </span>
        <span className="px-2 py-0.5 rounded-full text-[9px] tracking-[0.18em] uppercase bg-white/8 text-white/65">
          <Users size={9} className="inline mr-1"/> {hall.theater_hall_seats || "?"} seats
        </span>
        {(hall.theater_hall_stage_width && hall.theater_hall_stage_depth) ? (
          <span className="px-2 py-0.5 rounded-full text-[9px] tracking-[0.18em] uppercase bg-white/8 text-white/65">
            <Ruler size={9} className="inline mr-1"/> {hall.theater_hall_stage_width}×{hall.theater_hall_stage_depth}m
          </span>
        ) : null}
      </div>
      {canBook && (
        <Button onClick={onBook}
                className="mt-5 w-full h-10 rounded-full text-[10px] uppercase tracking-[0.22em] bg-violet-500 text-white hover:bg-violet-400"
                data-testid={`theater-hall-book-${hall.id}`}>
          <Calendar size={12} className="mr-2"/> Request Booking
        </Button>
      )}
    </div>
  );
}

function BookHallModal({ hall, productions, onClose, onBooked }) {
  const [form, setForm] = useState({
    production_id: "",
    requested_date: "",
    slot: "evening",
    offered_price: 25000,
    note: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.requested_date) { toast.error("Pick a date."); return; }
    setBusy(true);
    try {
      await api.post(`/theater-halls/${hall.id}/booking-requests`, {
        ...form,
        offered_price: Number(form.offered_price) || 0,
        currency: "INR",
      });
      onBooked();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit booking.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
         onClick={onClose} data-testid="theater-book-modal">
      <div className="w-full max-w-xl cs-card rounded-2xl p-6 sm:p-8 max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-violet-500/15 text-violet-400 flex items-center justify-center">
            <Theater size={18}/>
          </div>
          <div>
            <div className="font-display text-lg uppercase tracking-tight">Request Booking</div>
            <div className="text-[11px] text-white/55">at {hall.brand_name || hall.legal_name}</div>
          </div>
        </div>

        <div className="space-y-4">
          {productions.length > 0 && (
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Production</Label>
              <select value={form.production_id} onChange={set("production_id")}
                      className="w-full h-10 mt-1.5 px-3 rounded-md bg-black/40 border border-white/15 text-sm text-white"
                      data-testid="theater-book-prod">
                <option value="">— optional —</option>
                {productions.map((p) => (<option key={p.id} value={p.id}>{p.title}</option>))}
              </select>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Date *</Label>
              <Input type="date" value={form.requested_date} onChange={set("requested_date")}
                     className="bg-black/40 border-white/15 mt-1.5"
                     data-testid="theater-book-date"/>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Slot</Label>
              <select value={form.slot} onChange={set("slot")}
                      className="w-full h-10 mt-1.5 px-3 rounded-md bg-black/40 border border-white/15 text-sm text-white"
                      data-testid="theater-book-slot">
                <option value="matinee">Matinee</option>
                <option value="evening">Evening</option>
                <option value="full_day">Full Day</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Offered Price (₹)</Label>
              <Input type="number" min={0} step={500} value={form.offered_price} onChange={set("offered_price")}
                     className="bg-black/40 border-white/15 mt-1.5"
                     data-testid="theater-book-price"/>
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Note</Label>
            <textarea value={form.note} onChange={set("note")} rows={3}
                      placeholder="Anything the venue should know…"
                      className="w-full bg-black/40 border border-white/15 rounded-md p-2 mt-1.5 text-sm text-white outline-none focus:border-white/30"
                      data-testid="theater-book-note"/>
          </div>
        </div>

        <div className="mt-8 flex gap-3">
          <Button variant="ghost"
                  className="h-11 rounded-full text-[11px] uppercase tracking-[0.22em] text-white/70 hover:bg-white/5"
                  onClick={onClose}
                  data-testid="theater-book-cancel">Cancel</Button>
          <Button className="flex-1 h-11 rounded-full text-[11px] uppercase tracking-[0.22em] bg-violet-500 text-white hover:bg-violet-400 disabled:opacity-50"
                  disabled={busy} onClick={submit}
                  data-testid="theater-book-submit">
            {busy ? <><Loader2 size={13} className="animate-spin mr-2"/> Sending…</> : <>Send Request <ArrowRight size={13} className="ml-2"/></>}
          </Button>
        </div>
      </div>
    </div>
  );
}
